<template>
  <v-sheet class="d-flex flex-column" style="position:fixed;inset:0;z-index:100;">
    <scanner-header>
      <v-chip v-if="order_id" x-small outlined class="mr-2">#{{ order_id }}</v-chip>
      <v-btn icon small @click="closeDetail" v-if="order_id">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </scanner-header>

    <div class="d-flex flex-grow-1 overflow-hidden">

      <!-- Left: Order list (always visible) -->
      <div class="d-flex flex-column" :style="listStyle">

        <!-- Scan bar -->
        <div class="pa-3 flex-shrink-0">
          <v-text-field
            ref="pickslipInput"
            v-model="pickslip_buffer"
            outlined dense
            hide-details="auto"
            placeholder="Pickslip scannen oder ID eingeben..."
            inputmode="none"
            autocomplete="off"
            :error-messages="pickslip_error"
            :loading="is_loading_order"
            prepend-inner-icon="mdi-barcode-scan"
            append-icon="mdi-magnify"
            @click:append="onPickslipScan"
            @keydown.enter.prevent="onPickslipScan"
            @keydown.tab.prevent="onPickslipScan"
            @input="pickslip_error = null"
          />
        </div>

        <!-- Order list -->
        <v-progress-linear v-if="orders_loading" indeterminate color="primary" class="flex-shrink-0" />

        <v-list ref="orderList" dense class="flex-grow-1 overflow-y-auto pa-0">
          <v-list-item
            v-for="order in orders"
            :key="order.id"
            :class="{ 'v-list-item--active': order.id === order_id }"
            @click="selectOrder(order)"
          >
            <v-list-item-content>
              <v-list-item-title class="text-body-2 font-weight-bold">#{{ order.id }}</v-list-item-title>
              <v-list-item-subtitle class="text-caption">{{ formatDate(order.date_created) }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="my-0">
              <order-status-badge :value="order.status" size="x-small" />
            </v-list-item-action>
          </v-list-item>

          <v-list-item v-if="!orders_loading && !orders.length">
            <v-list-item-content class="text-center text-caption" style="opacity:0.5;">
              Keine Bestellungen gefunden
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <!-- Pagination -->
        <div class="d-flex flex-shrink-0">
          <v-btn
            tile depressed large
            class="flex-grow-1"
            :disabled="orders_options.page <= 1"
            @click="orders_options.page--"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn
            tile depressed large
            class="flex-grow-1"
            :disabled="orders_options.page >= Math.ceil(orders_total / orders_options.itemsPerPage) || orders_total <= 0"
            @click="orders_options.page++"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Order detail panel (single instance) -->
      <transition :name="isWide ? '' : 'slide-right'">
        <v-sheet
          v-if="order_id"
          class="d-flex flex-column"
          :style="detailPanelStyle"
        >
          <order-detail-panel
            :order-id="order_id"
            :order-data="order_data"
            :photos="photos"
            :is-uploading="is_uploading"
            :upload-status="upload_status"
            :loading="is_loading_detail"
            @update:photos="photos = $event"
            @upload="uploadPhotos"
            @close="closeDetail"
          />
        </v-sheet>
      </transition>

    </div>

    <v-snackbar v-model="toast.show" :color="toast.color" :timeout="2500" top>
      <div class="d-flex align-center">
        <v-icon left small>{{ toast.icon }}</v-icon>
        {{ toast.text }}
      </div>
    </v-snackbar>
  </v-sheet>
</template>

<script>
import EventService from '@/services/APIService'
import { sessionStore } from '@/store/pinia/sessionStore'
import ScannerHeader from '@/components/_scanner/ScannerHeader.vue'
import OrderStatusBadge from '@/components/_orders/infra/orderStatusBadge.vue'
import OrderDetailPanel from '@/components/_scanner/OrderDetailPanel.vue'

export default {
  name: 'StorageScannerPOC',

  components: { ScannerHeader, OrderStatusBadge, OrderDetailPanel },

  setup() {
    const session = sessionStore()
    return { session }
  },

  data() {
    return {
      // Pickslip
      pickslip_buffer: '',
      pickslip_error: null,
      is_loading_order: false,
      is_loading_detail: false,

      // Order
      order_id: null,
      order_data: null,

      // Photos
      photos: [],

      // Upload
      is_uploading: false,
      upload_status: null,

      // Order list
      orders: [],
      orders_total: 0,
      orders_loading: false,
      orders_options: { page: 1, itemsPerPage: 25, sortBy: ['date_created'], sortDesc: [true] },

      // Toast
      toast: { show: false, text: '', color: 'success', icon: 'mdi-check' },
    }
  },

  computed: {
    isWide() {
      return this.$vuetify.breakpoint.mdAndUp
    },
    listStyle() {
      return this.isWide ? { width: '40%', 'flex-shrink': 0 } : { width: '100%' }
    },
    detailPanelStyle() {
      if (this.isWide) {
        return { width: '60%', 'flex-shrink': 0, 'border-left': 'thin solid var(--v-border-base, rgba(0,0,0,0.12))' }
      }
      return { position: 'absolute', top: 0, right: 0, bottom: 0, width: '100%', 'z-index': 10 }
    },
  },

  mounted() {
    this.loadOrders()
    this.$nextTick(() => {
      if (this.$refs.pickslipInput) this.$refs.pickslipInput.focus()
    })
  },

  methods: {
    parseBarcode(raw) {
      const match = raw.match(/^([ICPWKARS])([0-9A-Z]+)$/i)
      if (match) return parseInt(match[2], 36).toString()
      return raw
    },

    async onPickslipScan() {
      const id = this.parseBarcode(this.pickslip_buffer.trim())
      if (!id) return

      this.is_loading_order = true
      this.pickslip_error = null
      try {
        const { data } = await EventService.post(`/api/v3/order/${id}`, {
          expand: ['line_items', 'line_items.related_object', 'pickslip_items']
        })
        const order = data?.payload || data

        this.order_id = order.id
        this.order_data = order
        this.showToast(`Bestellung #${order.id} geladen`, 'success', 'mdi-check')
      } catch (e) {
        this.pickslip_error = 'Bestellung nicht gefunden'
        this.pickslip_buffer = ''
        this.$nextTick(() => {
          if (this.$refs.pickslipInput) this.$refs.pickslipInput.focus()
        })
      } finally {
        this.is_loading_order = false
      }
    },

    dataUrlToBlob(dataUrl) {
      const [header, base64] = dataUrl.split(',')
      const mime = header.match(/:(.*?);/)[1]
      const binary = atob(base64)
      const array = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i)
      return new Blob([array], { type: mime })
    },

    async uploadPhotos() {
      this.is_uploading = true
      this.upload_status = null

      try {
        const formData = new FormData()
        formData.append('purpose', 'order-attachment')
        formData.append('purpose_id', this.order_id)
        formData.append('extra', JSON.stringify({ attachment_type: null }))

        this.photos.forEach((photo, i) => {
          const blob = this.dataUrlToBlob(photo.dataUrl)
          formData.append(`fileUpload-${i}`, blob, `photo_${i + 1}.jpg`)
        })

        await EventService.post('/api/v3/files/upload', formData)

        this.upload_status = 'success'
        this.showToast(`${this.photos.length} Foto(s) hochgeladen`, 'success', 'mdi-cloud-check')
        this.photos = []
      } catch (e) {
        this.upload_status = 'error'
        this.showToast('Upload fehlgeschlagen', 'error', 'mdi-alert-circle')
      } finally {
        this.is_uploading = false
      }
    },

    async selectOrder(order) {
      this.photos = []
      this.upload_status = null
      this.order_id = order.id
      this.order_data = order
      this.is_loading_detail = true

      try {
        const { data } = await EventService.post(`/api/v3/order/${order.id}`, {
          expand: ['line_items', 'line_items.related_object', 'pickslip_items']
        })
        this.order_data = data?.payload || data
      } catch (e) {
        // keep the search result data as fallback
      } finally {
        this.is_loading_detail = false
      }
    },

    closeDetail() {
      this.order_id = null
      this.order_data = null
      this.photos = []
      this.upload_status = null
      this.$nextTick(() => {
        if (this.$refs.pickslipInput) this.$refs.pickslipInput.focus()
      })
    },

    // --- Order list ---
    async loadOrders() {
      this.orders_loading = true
      this.orders = []
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.orders_options
        const orderby = {}
        if (sortBy.length) {
          orderby[sortBy[0]] = sortDesc[0] ? 'DESC' : 'ASC'
        }

        const { data } = await EventService.post('/api/v3/search/order', {
          object: 'order',
          scope: ['results'],
          params: {
            query: {
              status: ['processing', 'preparing-for-shipment'],
            },
            page,
            page_size: itemsPerPage,
            orderby,
          },
        })

        const payload = data?.payload || data
        this.orders = payload.results || []
        this.orders_total = payload.results_max || 0
      } catch (e) {
        this.orders = []
        this.orders_total = 0
      } finally {
        this.orders_loading = false
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },

    showToast(text, color = 'success', icon = 'mdi-check') {
      this.toast = { show: true, text, color, icon }
    },
  },

  watch: {
    orders_options: {
      handler() { this.loadOrders() },
      deep: true,
    },
  },
}
</script>

<style scoped>
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.25s ease;
}
.slide-right-enter,
.slide-right-leave-to {
  transform: translateX(100%);
}
</style>
